import React, { useState }    from 'react';
import { useCrewStore }        from '../../store/crewStore';
import { formatTime }          from '../../utils';
import { showToast }           from '../shared/Toast';
import AddDeliveryModal        from './modals/AddDeliveryModal';
import type { Delivery }       from '../../types';

interface Props {
  crewId: string;
  onBack: () => void;
}

const CrewDetail: React.FC<Props> = ({ crewId, onBack }) => {
  const { getById, updateCrew, removeCrew, setCurrentCrewId } = useCrewStore();
  const [showAddDelivery, setShowAddDelivery] = useState(false);

  const crew = getById(crewId);
  if (!crew) { onBack(); return null; }

  const deliveries = crew.deliveries || [];
  const bothReady  = crew.driver.confirmed && crew.sideman.confirmed;
  const initials   = (crew.driver.name.charAt(0) + crew.sideman.name.charAt(0)).toUpperCase();
  const done       = deliveries.filter(d => d.delivered && d.assembled).length;

  const handleConfirm = (role: 'driver' | 'sideman') => {
    updateCrew(crewId, c => ({
      ...c,
      [role]: { ...c[role], confirmed: true, confirmedAt: new Date().toISOString() },
    }));
    showToast(`${role === 'driver' ? 'Driver' : 'Sideman'} confirmed!`);
  };

  const handleDelete = () => {
    if (!confirm('Remove this crew pair and all their deliveries? This cannot be undone.')) return;
    removeCrew(crewId);
    showToast('Crew pair removed');
    setCurrentCrewId(null);
  };

  const toggleDelivery = (did: string, field: 'delivered' | 'assembled') => {
    updateCrew(crewId, c => ({
      ...c,
      deliveries: (c.deliveries || []).map(d => {
        if (d.id !== did) return d;
        const updated = { ...d, [field]: !d[field] };
        if (updated.delivered && updated.assembled && !updated.completedAt) {
          updated.completedAt = new Date().toISOString();
        } else if (!updated.delivered || !updated.assembled) {
          updated.completedAt = null;
        }
        return updated;
      }),
    }));
  };

  const removeDelivery = (did: string) => {
    if (!confirm('Remove this delivery address?')) return;
    updateCrew(crewId, c => ({ ...c, deliveries: (c.deliveries || []).filter(d => d.id !== did) }));
    showToast('Address removed');
  };

  return (
    <div>
      {/* HEADER */}
      <div className="detail-header">
        <div className="detail-nav">
          <button className="back-btn" onClick={onBack}>
            <svg viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>
            Back
          </button>
          <button className="delete-crew-btn" onClick={handleDelete}>
            <svg viewBox="0 0 24 24"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>
            Remove
          </button>
        </div>
        <div className="detail-identity">
          <div className="detail-badge">
            <div className="detail-badge-icon">{initials}</div>
            <div className="detail-badge-label">Crew</div>
          </div>
          <div className="detail-names">
            <div className="detail-title">{crew.driver.name} &amp; {crew.sideman.name}</div>
            <div className="detail-meta">{crew.workDate} · {deliveries.length} address{deliveries.length !== 1 ? 'es' : ''}</div>
          </div>
        </div>
      </div>

      <div className="detail-body">
        {/* STATUS BANNER */}
        {bothReady ? (
          <div className="banner banner-success">
            <svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>
            Both crew members confirmed — ready for tomorrow
          </div>
        ) : (
          <div className="banner banner-warning">
            <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/><path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/></svg>
            Awaiting confirmation from {!crew.driver.confirmed && !crew.sideman.confirmed ? 'both crew members' : (!crew.driver.confirmed ? 'driver' : 'sideman')}
          </div>
        )}

        {/* CONFIRMATION */}
        <div className="section-block">
          <div className="section-hd">
            <div className="section-label">Crew Confirmation</div>
          </div>
          <div className="confirm-grid">
            {(['driver','sideman'] as const).map(role => {
              const m = crew[role];
              return (
                <div key={role} className={`confirm-card ${m.confirmed ? 'confirmed-state' : ''}`}>
                  <div className="cc-role">{role === 'driver' ? 'Driver' : 'Sideman'}</div>
                  <div className="cc-name">{m.name}</div>
                  <button
                    className={`cc-btn ${m.confirmed ? 'is-confirmed' : 'not-confirmed'}`}
                    disabled={m.confirmed}
                    onClick={() => handleConfirm(role)}
                  >
                    {m.confirmed ? (
                      <><svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>Confirmed</>
                    ) : (
                      <><svg viewBox="0 0 24 24"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14zm-7 11H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>Confirm Ready</>
                    )}
                  </button>
                  {m.confirmed && <div className="cc-time">Confirmed at {formatTime(m.confirmedAt)}</div>}
                </div>
              );
            })}
          </div>
        </div>

        {/* DELIVERIES */}
        <div className="section-block">
          <div className="section-hd">
            <div className="section-label">Delivery Addresses</div>
            <div className="section-count-pill">{done}/{deliveries.length} done</div>
          </div>
          <button className="add-address-btn" onClick={() => setShowAddDelivery(true)}>
            <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none"/></svg>
            Add Delivery Address
          </button>
          <div className="delivery-list">
            {deliveries.length === 0 ? (
              <div style={{textAlign:'center',padding:'28px 0',fontSize:13,color:'var(--text-muted)'}}>No addresses added yet</div>
            ) : (
              deliveries.map((d, i) => (
                <DeliveryItem
                  key={d.id}
                  delivery={d}
                  index={i}
                  onToggle={toggleDelivery}
                  onRemove={removeDelivery}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {showAddDelivery && (
        <AddDeliveryModal
          crewId={crewId}
          onClose={() => setShowAddDelivery(false)}
        />
      )}
    </div>
  );
};

interface DeliveryItemProps {
  delivery: Delivery;
  index:    number;
  onToggle: (id: string, field: 'delivered' | 'assembled') => void;
  onRemove: (id: string) => void;
}

const DeliveryItem: React.FC<DeliveryItemProps> = ({ delivery, index, onToggle, onRemove }) => {
  const complete = delivery.delivered && delivery.assembled;
  return (
    <div className={`delivery-item ${complete ? 'all-done' : ''}`}>
      <div className="di-header">
        <div className="di-num">Stop {index + 1}</div>
        <div className="di-address">{delivery.address}</div>
        <button className="di-del-btn" onClick={() => onRemove(delivery.id)}>
          <svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/></svg>
        </button>
      </div>
      <div className="di-checks">
        {(['delivered','assembled'] as const).map(field => (
          <label
            key={field}
            className={`check-toggle ${delivery[field] ? 'ticked' : ''}`}
            onClick={() => onToggle(delivery.id, field)}
          >
            <input type="checkbox" readOnly checked={delivery[field]} />
            <div className="check-box-ui">
              <svg viewBox="0 0 12 12"><path d="M2 6l2.5 2.5L10 3" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>
            </div>
            <span className="check-label-txt">{field === 'delivered' ? 'Delivered' : 'Assembled'}</span>
          </label>
        ))}
      </div>
      {complete && delivery.completedAt && (
        <div className="di-footer">Completed at {formatTime(delivery.completedAt)}</div>
      )}
    </div>
  );
};

export default CrewDetail;
